import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getCompetitionDetails } from '../services/public-api';
import { updateCompetition } from '../services/organizer-api';
import Container from '../components/layout/Container';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import './CreateCompetition.css'; // Reuse same styles

/**
 * EditCompetition
 * Form for organisers to edit existing competitions
 */
export default function EditCompetition() {
  const navigate = useNavigate();
  const { competitionId } = useParams();
  const { user, isOrganizer } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [originalDivisions, setOriginalDivisions] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    registrationStartDate: '',
    registrationEndDate: '',
    resultsDeadlineDate: '',
  });

  useEffect(() => {
    if (competitionId) {
      loadCompetition();
    }
  }, [competitionId]);

  const loadCompetition = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getCompetitionDetails(competitionId);
      setCompetition(data.competition);
      
      // Load divisions - the API returns them separately
      const divisionNames = data.divisions?.map(d => d.name) || [];
      setDivisions(divisionNames);
      setOriginalDivisions(divisionNames);
      
      // Populate form with existing data
      setFormData({
        name: data.competition.name || '',
        startDate: data.competition.startDate || '',
        endDate: data.competition.endDate || '',
        registrationStartDate: data.competition.registrationStartDate || '',
        registrationEndDate: data.competition.registrationEndDate || '',
        resultsDeadlineDate: data.competition.resultsDeadlineDate || '',
      });
    } catch (err) {
      console.error('Failed to load competition:', err);
      setError(err.message || 'Failed to load competition');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const updates = {
        name: formData.name,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        registrationStartDate: formData.registrationStartDate || undefined,
        registrationEndDate: formData.registrationEndDate || undefined,
        resultsDeadlineDate: formData.resultsDeadlineDate || undefined,
      };

      await updateCompetition(competitionId, updates);

      // Redirect to organiser dashboard on success
      navigate('/organizer/dashboard');
    } catch (err) {
      console.error('Failed to update competition:', err);
      setError(err.message || 'Failed to update competition');
    } finally {
      setSaving(false);
    }
  };

  if (!user || !isOrganizer) {
    return (
      <Container>
        <div className="auth-required">
          <h2>Access Denied</h2>
          <p>This page is only accessible to competition organisers.</p>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Loading size="large" />
      </Container>
    );
  }

  if (error && !competition) {
    return (
      <Container>
        <ErrorMessage message={error} onRetry={loadCompetition} />
      </Container>
    );
  }

  return (
    <Container maxWidth="narrow">
      <div className="create-competition">
        <div className="page-header">
          <h1>Edit Competition</h1>
          <p className="page-description">
            Update competition details
          </p>
        </div>

        {error && (
          <ErrorMessage message={error} variant="error" />
        )}

        <form onSubmit={handleSubmit} className="competition-form">
          {/* Basic Information */}
          <section className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label htmlFor="name">Competition Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Spring Pool League 2025"
              />
            </div>

            {competition && (
              <div className="form-group">
                <label>Current Status</label>
                <div className="read-only-field">
                  <strong>{competition.status}</strong>
                </div>
                <small className="form-help">
                  Status automatically updates based on the dates below
                </small>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="registrationStartDate">Registration Start Date</label>
              <input
                type="date"
                id="registrationStartDate"
                name="registrationStartDate"
                value={formData.registrationStartDate}
                onChange={handleChange}
              />
              <small className="form-help">
                Competition moves to "open_registration" on this date
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="registrationEndDate">Registration End Date</label>
              <input
                type="date"
                id="registrationEndDate"
                name="registrationEndDate"
                value={formData.registrationEndDate}
                onChange={handleChange}
              />
              <small className="form-help">
                Competition moves to "registration_closed" on this date
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="startDate">Start Date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
              />
              <small className="form-help">
                Competition moves to "active" on this date
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
              <small className="form-help">
                Competition moves to "results_pending" on this date
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="resultsDeadlineDate">Results Deadline Date</label>
              <input
                type="date"
                id="resultsDeadlineDate"
                name="resultsDeadlineDate"
                value={formData.resultsDeadlineDate}
                onChange={handleChange}
              />
              <small className="form-help">
                Competition moves to "completed" on this date
              </small>
            </div>
          </section>

          {/* Divisions Section */}
          {competition && divisions.length > 0 && (
            <section className="form-section">
              <h2>Divisions</h2>
              <div className="divisions-list">
                {divisions.map((division, index) => (
                  <div key={index} className="division-tag">
                    <span>{division}</span>
                  </div>
                ))}
              </div>
              <small className="form-help">
                Note: Division management is currently read-only. Contact support to modify divisions.
              </small>
            </section>
          )}

          {/* Read-only Information */}
          {competition && (
            <section className="form-section">
              <h2>Competition Details (Read-Only)</h2>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Type</span>
                  <span className="info-value">{competition.competitionType}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Start Date</span>
                  <span className="info-value">
                    {new Date(competition.startDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Players Per Team</span>
                  <span className="info-value">{competition.playersPerTeam}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Divisions</span>
                  <span className="info-value">{divisions.length}</span>
                </div>
              </div>
            </section>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/organizer/dashboard')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Update Competition'}
            </Button>
          </div>
        </form>
      </div>
    </Container>
  );
}
